import { useMemo, useState } from 'react';
import { Mail, MessageSquare } from 'lucide-react';
import {
  MAIL_LOG_DELIVERIES,
  SystemRole,
  hasMinimumRole,
  type MailLogChannel,
  type MailLogDelivery,
  type MailLogEntry,
  type MailLogFilters,
  type MailLogStatus,
} from '@peles/shared';
import { EntityList, FilterSelect, StatusBadge } from '../common';
import type { EntityListColumn } from '../common';
import { useAuth } from '../../context/AuthContext';
import { useMailCategories, useMailLog } from '../../hooks/useMailLog';
import { MailLogDetail } from './MailLogDetail';
import {
  DELIVERY_COLOR,
  DELIVERY_LABEL,
  STATUS_COLOR,
  STATUS_HINT,
  STATUS_LABEL,
  categoryLabel,
  formatWhen,
} from './mailLogFormat';

const CHANNEL_OPTIONS: { id: MailLogChannel; label: string }[] = [
  { id: 'email', label: 'Email' },
  { id: 'sms', label: 'Text message' },
];

const STATUS_OPTIONS: { id: MailLogStatus; label: string }[] = (
  ['sent', 'failed', 'suppressed', 'simulated', 'queued'] as const
).map((id) => ({ id, label: STATUS_LABEL[id] }));

const DELIVERY_OPTIONS = MAIL_LOG_DELIVERIES.map((id) => ({ id, label: DELIVERY_LABEL[id] }));

const PAGE_SIZE = 25;

export interface MailLogViewProps {
  /** Narrow to one event: its own messages and every guest's. */
  eventId?: string;
  /** Narrow to one guest. */
  guestId?: string;
  /** Heading above the list. */
  title?: string;
}

/**
 * Every email and text the application sent — invitations, reminders,
 * broadcasts, confirmations, vendor emails, conversation notices — with what
 * became of each, from one log (`/mail-log`).
 *
 * The same component is the administrator's whole log (no scope) and an
 * event's "Message history" (`eventId`), so the two never disagree about what
 * was sent. Producers can send a message again from its detail.
 *
 * Filters are the ways the question gets asked: by channel, by outcome, by
 * what the provider reported afterwards, by which kind of message, by date,
 * or by searching the words somebody remembers.
 */
export function MailLogView({ eventId, guestId, title }: MailLogViewProps) {
  const { user } = useAuth();
  const canResend = user != null && hasMinimumRole(user.systemRole, SystemRole.PLANNER);

  const [channels, setChannels] = useState<MailLogChannel[]>([]);
  const [statuses, setStatuses] = useState<MailLogStatus[]>([]);
  const [deliveries, setDeliveries] = useState<MailLogDelivery[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [openId, setOpenId] = useState<string | null>(null);

  const scope = useMemo(
    () => ({ ...(eventId ? { eventId } : {}), ...(guestId ? { guestId } : {}) }),
    [eventId, guestId],
  );

  const filters: MailLogFilters = useMemo(
    () => ({
      ...scope,
      channels,
      statuses,
      deliveries,
      categories,
      ...(search.trim() ? { search: search.trim() } : {}),
      // Dates as the picker gives them; the server reads a bare "to" date as
      // through the end of that day.
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      limit: pageSize,
      offset: (page - 1) * pageSize,
    }),
    [scope, channels, statuses, deliveries, categories, search, from, to, page, pageSize],
  );

  const { data, isLoading, isError } = useMailLog(filters);
  const { data: categoryCounts } = useMailCategories({ ...filters, categories: [] });

  const categoryOptions = useMemo(
    () =>
      (categoryCounts ?? [])
        .filter((row) => row.category !== null)
        .map((row) => ({
          id: row.category as string,
          label: `${categoryLabel(row.category)} (${row.count})`,
        })),
    [categoryCounts],
  );

  /** Every filter change starts again from the first page. */
  function change<T>(setter: (value: T) => void) {
    return (value: T) => {
      setPage(1);
      setter(value);
    };
  }

  const columns: EntityListColumn<MailLogEntry>[] = [
    {
      key: 'channel',
      header: '',
      width: '2rem',
      render: (entry) =>
        entry.channel === 'sms' ? (
          <MessageSquare className="w-4 h-4 text-gray-400" aria-label="Text message" />
        ) : (
          <Mail className="w-4 h-4 text-gray-400" aria-label="Email" />
        ),
    },
    {
      key: 'to',
      header: 'To',
      render: (entry) => (
        <div className="min-w-0">
          {entry.guestName && <div className="font-medium text-gray-900">{entry.guestName}</div>}
          <div className="text-xs text-gray-500 break-all">{entry.to.join(', ')}</div>
        </div>
      ),
    },
    {
      key: 'subject',
      header: 'Message',
      render: (entry) => (
        <div className="min-w-0">
          <div className="text-gray-900 truncate">{entry.subject || '(no subject)'}</div>
          <div className="text-xs text-gray-500">{categoryLabel(entry.category)}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (entry) => (
        <div className="space-y-0.5" title={STATUS_HINT[entry.status]}>
          <div className="flex flex-wrap gap-1">
            <StatusBadge label={STATUS_LABEL[entry.status]} color={STATUS_COLOR[entry.status]} />
            {entry.delivery && (
              <StatusBadge
                label={DELIVERY_LABEL[entry.delivery]}
                color={DELIVERY_COLOR[entry.delivery]}
              />
            )}
          </div>
          {entry.error && (
            <div
              className={`text-xs truncate ${entry.status === 'sent' ? 'text-amber-700' : 'text-red-600'}`}
              title={entry.error}
            >
              {entry.error}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'sentAt',
      header: 'Sent',
      hideOnMobile: true,
      render: (entry) => (
        <span className="text-sm text-gray-600">{formatWhen(entry.sentAt ?? entry.createdAt)}</span>
      ),
    },
  ];

  const filtered =
    search.trim() !== '' ||
    channels.length + statuses.length + deliveries.length + categories.length > 0 ||
    from !== '' ||
    to !== '';

  return (
    <div className="space-y-3" data-testid="mail-log">
      <EntityList
        title={title}
        items={data?.items ?? []}
        columns={columns}
        getKey={(entry) => entry.id}
        isLoading={isLoading}
        onRowClick={(entry) => setOpenId(entry.id)}
        searchValue={search}
        onSearchChange={change(setSearch)}
        searchPlaceholder="Search recipient, subject or message text…"
        page={page}
        pageSize={pageSize}
        total={data?.total ?? 0}
        onPageChange={setPage}
        pageSizeOptions={[25, 50, 100]}
        onPageSizeChange={change(setPageSize)}
        emptyMessage={
          isError
            ? 'The log could not be loaded.'
            : `Nothing sent yet${filtered ? ' matching these filters' : ''}.`
        }
        emptyIcon={<Mail className="w-8 h-8 text-gray-300" />}
        filterBar={
          <div className="flex flex-wrap items-center gap-2">
            <FilterSelect
              label="Channel"
              items={CHANNEL_OPTIONS}
              value={channels}
              onChange={(value) => change(setChannels)(value as MailLogChannel[])}
              getValue={(option) => option.id}
              getLabel={(option) => option.label}
              multiple
            />
            <FilterSelect
              label="Status"
              items={STATUS_OPTIONS}
              value={statuses}
              onChange={(value) => change(setStatuses)(value as MailLogStatus[])}
              getValue={(option) => option.id}
              getLabel={(option) => option.label}
              multiple
            />
            <FilterSelect
              label="Delivery"
              items={DELIVERY_OPTIONS}
              value={deliveries}
              onChange={(value) => change(setDeliveries)(value as MailLogDelivery[])}
              getValue={(option) => option.id}
              getLabel={(option) => option.label}
              multiple
            />
            {categoryOptions.length > 0 && (
              <FilterSelect
                label="Type"
                items={categoryOptions}
                value={categories}
                onChange={(value) => change(setCategories)(value as string[])}
                getValue={(option) => option.id}
                getLabel={(option) => option.label}
                multiple
                searchable
              />
            )}
            <input
              type="date"
              value={from}
              aria-label="Sent from"
              onChange={(e) => change(setFrom)(e.target.value)}
              className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="date"
              value={to}
              aria-label="Sent until"
              onChange={(e) => change(setTo)(e.target.value)}
              className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        }
      />
      <MailLogDetail
        id={openId}
        scope={scope}
        canResend={canResend}
        onClose={() => setOpenId(null)}
      />
    </div>
  );
}
